import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, MoveLeft } from "lucide-react";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Skeleton,
} from "@mui/material";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";

import ActivityOrgAdmin from "../shared_components/BulkActivity";
import SourcedCandidates from "../orgadmin/SourcedCandidates";
import Loader from "../Loader";
import host from "../../global";
import DropDownWithSearch from "../shared/DropDownWIthSearch";
import { useTheme } from "../../hooks/useTheme";

const JobApplicants = () => {
  // Hooks and navigation
  const location = useLocation();
  const navigate = useNavigate();
  const accessToken = Cookies.get("accessToken");
  const portal = location.pathname.startsWith("/hr") ? "hr" : "org-admin";

  // Refs
  const jdRef = useRef(null);

  // State management
  const [jobRoleData, setJobRoleData] = useState(null);
  const [jobPositions, setJobPositions] = useState({});
  const [selectedJobPosition, setSelectedJobPosition] =
    useState("All Positions");
  const [selectedID, setSelectedID] = useState("all");
  const [activeTab, setActiveTab] = useState(1);
  const [showJD, setShowJD] = useState(false);
  const [jobDescription, setJobDescription] = useState("");

  // Loading states
  const [isPositionsLoading, setIsPositionsLoading] = useState(false);
  const [isJobDescLoading, setIsJobDescLoading] = useState(false);


  const { theme } = useTheme();

  // Utility Functions
  const filterActiveJobPositions = (jobDescriptions) => {
    return Object.entries(jobDescriptions)
      .filter(([_, jd]) => {
        const isActive = jd.jd_status?.toLowerCase() === "active";

        // For V2 jobs: must be both active AND finalized
        if (jd.job_description_type === "v2") {
          const isFinalized = jd.status === "finalized";
          return isActive && isFinalized;
        }

        // For V1 jobs: just check active status
        return isActive;
      })
      .reduce((acc, [jd_id, jd]) => {
        acc[jd_id] = {
          job_role: jd.job_role,
          jd_status: jd.jd_status,
          posting_date: jd.posting_date,
          experience_required: jd.experience_required,
          job_description_type: jd.job_description_type,
        };
        return acc;
      }, {});
  };

  const createJobDataObject = (jobId, jobData) => ({
    jd_id: jobId,
    job_role: jobData.job_role,
    jobrole: jobData.job_role,
    jd_status: jobData.jd_status,
    posting_date: jobData.posting_date,
    experience_required: jobData.experience_required,
    job_description_type: jobData.job_description_type,
  });

  const updateBrowserState = (jobData) => {
    try {
      window.history.replaceState(jobData, "", window.location.pathname);
      console.log("Updated browser state with job data:", jobData);

      // Trigger custom event to notify ActivityOrgAdmin
      const event = new CustomEvent("jobRoleChanged", { detail: jobData });
      window.dispatchEvent(event);
      console.log("Job role change event dispatched");
    } catch (error) {
      console.warn("Could not update history state:", error);
    }
  };

  // Error Handling
  const handleAPIError = async (error, operation) => {
    console.error(`Error ${operation}:`, error);

    if (error.response) {
      const status = error.response.status;
      const detail = error.response.data.detail || "Unknown error";

      if (status === 401) {
        Cookies.remove("accessToken");
        toast.error(`Unauthorized: ${detail}`);
      } else if (status === 404) {
        toast.warning(`Not Found: ${detail}`);
      } else {
        toast.error(`Error: ${detail}`);
      }
    } else {
      toast.error("An unexpected error occurred. Please try again.");
    }
  };

  // Job Management Functions
  const selectJob = (jobId, jobData, source = "manual") => {
    console.log(`Selecting job (${source}):`, jobId, jobData);

    if (!jobId || !jobData) {
      console.error("Invalid job selection - missing jobId or jobData:", {
        jobId,
        jobData,
      });
      return null;
    }

    const newJobData = createJobDataObject(jobId, jobData);
    console.log("Created job data object:", newJobData);

    setSelectedID(jobId);
    setSelectedJobPosition(jobData.job_role);
    setJobRoleData(newJobData);
    updateBrowserState(newJobData);

    // Fetch job description
    setTimeout(() => getJobDescription(jobId), 100);

    return newJobData;
  };

  const fetchJobPositions = async () => {
    setIsPositionsLoading(true);

    try {
      const response = await axios.get(`${host}/getjdpositions`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const jobDescriptions = response.data || {};
      const activeJobPositions = filterActiveJobPositions(jobDescriptions);

      console.log("Active Job Positions:", activeJobPositions);
      setJobPositions(activeJobPositions);

      // Handle initial job selection
      if (Object.keys(activeJobPositions).length > 0) {
        let jobToSelect = null;

        // Priority 1: Use location state if valid
        if (
          location?.state?.jd_id &&
          activeJobPositions[location.state.jd_id]
        ) {
          jobToSelect = {
            id: location.state.jd_id,
            data: activeJobPositions[location.state.jd_id],
            source: "location_state",
          };
          selectJob(jobToSelect.id, jobToSelect.data, jobToSelect.source);
        }
        // Default: Keep "All" selected - create All job data for components
        else {
          const allJobData = {
            jd_id: "all",
            job_role: "All Job Roles",
            jobrole: "All Job Roles",
            jd_status: "active",
            posting_date: new Date().toISOString(),
            experience_required: "All Levels",
            job_description_type: "all",
          };
          setJobRoleData(allJobData);
          updateBrowserState(allJobData);
        }
      }
    } catch (error) {
      await handleAPIError(error, "fetching job positions");
    } finally {
      setIsPositionsLoading(false);
    }
  };

  const getJobDescription = async (jdId = null) => {
    const jobId = jdId || selectedID || jobRoleData?.jd_id;
    if (!jobId) {
      console.warn("No job ID provided for fetching job description");
      return;
    }

    console.log("Fetching job description for:", jobId);
    setIsJobDescLoading(true);

    try {
      const response = await axios.get(`${host}/getjd`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: { jd_id: jobId },
      });

      if (response?.data) {
        console.log("Job description fetched successfully");
        setJobDescription(response.data);
      }
    } catch (error) {
      await handleAPIError(error, "fetching job description");
    } finally {
      setIsJobDescLoading(false);
    }
  };

  // Event Handlers
  const handleJobPositionChange = async (e) => {
    // console.log({ e })
    const selectedJobId = e.value;

    console.log(
      "Job selection changed:",
      selectedJobId,
      "Current active tab:",
      activeTab
    );

    if (selectedJobId === "") {
      console.warn("Invalid job selection:", selectedJobId);
      return;
    }

    try {
      setJobDescription("");
      // Keep current active tab when job role changes - don't reset to tab 1

      if (selectedJobId === "all") {
        // Handle "All" selection
        const allJobData = {
          jd_id: "all",
          job_role: "All Job Roles",
          jobrole: "All Job Roles",
          jd_status: "active",
          posting_date: new Date().toISOString(),
          experience_required: "All Levels",
          job_description_type: "all",
        };
        setSelectedID("all");
        setSelectedJobPosition("All Job Roles");
        setJobRoleData(allJobData);
        updateBrowserState(allJobData);
        // toast.success("Switched to All Job Roles");
      } else {
        // Handle specific job selection
        const selectedPosition = jobPositions[selectedJobId];
         console.log("==================================================================================================================")
         console.log({selectedJobPosition,jobPositions,selectedJobId})
        if (!selectedPosition) {
          console.warn("Invalid job position:", selectedJobId);
          return;
        }
        selectJob(selectedJobId, selectedPosition, "user_selection");
        await getJobDescription(selectedJobId);
        // toast.success(`Switched to ${selectedPosition.job_role}`);
      }
    } catch (error) {
      console.error("Error handling job position change:", error);
      toast.error("Failed to change job position. Please try again.");
    }
  };

  const handleUploadResumeClick = () => {
    const route =
      portal === "org-admin" ? "/org-admin/checkresume" : "/hr/checkresume";

    if (!jobRoleData?.jd_id) {
      toast.error("Please select a job role first");
      return;
    }

    const jobId = jobRoleData.jd_id;
    const jobPosition = jobPositions[jobId];

    navigate(route, {
      state: {
        selectedJobId: jobId,
        autoSelectJob: true,
        jobRoleData: jobRoleData, 
        jobPosition: jobPosition,
      },
    });
  };

  // Initialize page on component mount
  useEffect(() => {
    const initializePage = async () => {
      try {
        console.log("Initializing JobApplicants page");

        // Set initial job role data from location state if available
        if (location?.state?.jd_id) {
          console.log(
            "Setting initial job data from location:",
            location.state
          );
          setJobRoleData(location.state);
        }

        await fetchJobPositions();
      } catch (error) {
        console.error("Error initializing page:", error);
        toast.error("Failed to initialize page. Please try again.");
      }
    };

    initializePage();
  }, []);

  // Handle clicks outside JD modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (jdRef.current && !jdRef.current.contains(event.target)) {
        setShowJD(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Format job description for display
  const formatJobDescription = (jobRole, data) => {
    console.log(data);
    if (data?.questionType === "screening" || data?.questionType === "Initial Screening" || data?.questionType === "Bring Your Own Questions") {
      return (
        <pre className="font-sans dark:glass-effect bg-bgSecondary text-md whitespace-pre-wrap break-words p-6 rounded-2xl shadow-inner dark:text-white text-textPrimary">
          {`Job Description:\n${data.description?.Raw_Job_Description || data.description.job_description || "- None"}\n\n`}
        </pre>
      );
    }
    if (data.description.Required_Skills?.skill_based) {
      const skillBasedData = data.description.Required_Skills?.skill_based || [];
      const skillsDisplay = skillBasedData
        .map((skill) => {
          const topics =
            skill.topics && skill.topics.length > 0
              ? ` (${skill.topics.join(", ")})`
              : "";
          return `• ${skill.skill_name}${topics}`;
        })
        .join("\n");

      return (
        <pre className="font-sans dark:glass-effect bg-bgSecondary text-md whitespace-pre-wrap break-words p-6 rounded-2xl shadow-inner text-white">
          {`Required Skills:\n${skillsDisplay || "- None"}\n\n${data.description.Roles_and_Responsibilities?.length
            ? `Roles & Responsibilities:\n${data.description.Roles_and_Responsibilities.map(
              (item, i) => `${i + 1}. ${item}`
            ).join("\n")}`
            : ""
            }\n\n${data.description.Tools_and_Technologies?.length
              ? `Tools & Technologies:\n  ${data.description.Tools_and_Technologies.join(
                ", "
              )}\n\n`
              : ""
            }${data.description.GoodtoHave?.length
              ? `Good to Have:\n  ${data.description.GoodtoHave.join(", ")}\n\n`
              : ""
            }`}
        </pre>
      );
    } else {
      const experience = (data.description.minExp + data.description.maxExp) / 2;
      return (
        <pre className="font-sans dark:glass-effect text-md whitespace-pre-wrap break-words p-6 rounded-2xl shadow-inner dark:text-white text-textPrimary">
          {`Required Skills:\n${experience < 4
            ? data.description.Required_Skills?.Medium?.length
              ? data.description.Required_Skills.Medium.map(
                (skill, i) => `${i + 1}. ${skill}`
              ).join("\n")
              : "- None"
            : data.description.Required_Skills?.Hard?.length
              ? data.description.Required_Skills.Hard.map(
                (skill, i) => `${i + 1}. ${skill}`
              ).join("\n")
              : "- None"
            }\n\nOptional Skills:\n${experience < 4
              ? data.description.Required_Skills?.Easy?.join(", ") || "None"
              : data.description.Required_Skills?.Medium?.join(", ") || "None"
            }\n\n${data.description.Roles_and_Responsibilities?.length
              ? `Roles & Responsibilities:\n${data.description.Roles_and_Responsibilities.map(
                (item, i) => `${i + 1}. ${item}`
              ).join("\n")}`
              : ""
            }\n\n${data.description.Tools_and_Technologies?.length
              ? `Tools & Technologies:\n  ${data.description.Tools_and_Technologies.join(
                ", "
              )}\n\n`
              : ""
            }${data.description.GoodtoHave?.length
              ? `Good to Have:\n  ${data.description.GoodtoHave.join(", ")}\n\n`
              : ""
            }`}
        </pre>
      );
    }
  };

  const dropdownData = Object.entries(jobPositions).map(([id, position]) => ({
    value: id,
    label: position.job_role,
  }));

  const modalJobTitle =
    jobRoleData?.jobrole || jobRoleData?.job_role || "Job Description";
  const modalExperience =
  jobDescription?.description?.minExp != null &&
  jobDescription?.description?.maxExp != null
    ? jobDescription.description.minExp === 0 &&
      jobDescription.description.maxExp === 0
      ? "0 yrs"
      : `${jobDescription.description.minExp} - ${jobDescription.description.maxExp} yrs`
    : jobRoleData?.experience_required || "";

  return (
    <div className="min-h-screen border rounded-2xl border-gray-700/50 dark:glass-effect dark:text-white bg-bgSecondary text-textPrimary px-8 transition-colors">
      {/* Header Section */}
      <div className="flex items-center justify-between py-6">
        <div className="flex items-center space-x-4 flex-1">
          <div className="flex items-center space-x-4 flex-1">
            <h1 className="text-xl dark:text-[#D9D9D9] text-textPrimary font-normal">
              Job Applicants
            </h1>

            <DropDownWithSearch
              className="dark:glass-effect bg-white/30 backdrop-blur-sm rounded-3xl z-[99]"
              value={selectedID}
              placeholder="Select Job Role"
              dropdownData={dropdownData}
              style={{
                fontFamily: "Poppins",
                borderRadius: "30px",
                color: "rgba(216, 216, 216, 1)",
                background:
                  "linear-gradient(233.57deg, rgba(34, 52, 97, 0.25) -5.01%, rgba(11, 10, 31, 0.05) 100%)",
                backdropFilter: "blur(500px)",
              }}
              onSelect={handleJobPositionChange}
            />
            {/* Job Position Selector */}
            {/* <Select
              value={selectedID}
              onChange={handleJobPositionChange}
              className="glass-effect bg-white/30 backdrop-blur-sm rounded-xl"
              style={{
                fontFamily: "Poppins",
                borderRadius: "8px",
                color: "rgba(216, 216, 216, 1)",
                background:
                  "linear-gradient(233.57deg, rgba(34, 52, 97, 0.25) -5.01%, rgba(11, 10, 31, 0.05) 100%)",
                backdropFilter: "blur(500px)",
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    background:
                      "linear-gradient(233.57deg, rgba(34, 52, 97, 0.25) -5.01%, rgba(11, 10, 31, 0.05) 100%)",
                    opacity: 1,
                    backdropFilter: "blur(500px)",
                    color: "rgba(216, 216, 216, 1)",
                    maxHeight: "200px",
                    width: "288px",
                    border: "1px solid rgba(229, 229, 229, 0.3)", // ✅ subtle border for dropdown
                    borderRadius: "10px",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
                  },
                },
              }}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(229, 229, 229, 0.5)", // ✅ normal border
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(229, 229, 229, 0.75)",
                },
                // 🚀 disable that ugly white border when focused/open
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(229, 229, 229, 0.5) !important",
                },
                "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                  borderWidth: "1px !important",
                },
                "& .MuiSvgIcon-root.MuiSelect-icon": {
                  color: "rgba(216, 216, 216, 1) !important",
                },
                "& .MuiSelect-select": {
                  fontFamily: "Poppins",
                  color: "rgba(216, 216, 216, 1)",
                  padding: "8px 14px",
                  minHeight: "20px",
                },
                "& .MuiInputBase-root": {
                  minHeight: "40px",
                  height: "40px",
                },
                width: "288px",
              }}
            >
              <MenuItem key="all" value="all">
                All Job Roles
              </MenuItem>

              {isPositionsLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <MenuItem key={index} disabled>
                    <Skeleton variant="text" width="100%" />
                  </MenuItem>
                ))
              ) : Object.keys(jobPositions).length > 0 ? (
                Object.entries(jobPositions).map(([id, position]) => (
                  <MenuItem key={id} value={id}>
                    {position.job_role}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>
                  <em style={{ color: "rgba(216, 216, 216, 0.7)" }}>
                    No positions available
                  </em>
                </MenuItem>
              )}
            </Select> */}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-4">
          <button
            className={`${theme === 'dark' ? 'button' : 'border-btn !text-textPrimary'} ${selectedID === "all" ? "opacity-50 cursor-not-allowed" : ""
              }`}
            onClick={() => selectedID !== "all" && setShowJD(true)}
            disabled={selectedID === "all"}
            title={
              selectedID === "all"
                ? "Select a specific job role to view JD"
                : "View Job Description"
            }
          >
            <span>View JD</span>
          </button>
          <button
            onClick={handleUploadResumeClick}
            className="px-6 py-2.5 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full transition-colors text-sm"
          >
            Upload Resume
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex items-center space-x-4 border-b border-[#5A4479A3]">
        <div className="flex items-center mb-1.5">
          <div
            onClick={() => setActiveTab(1)}
            className="px-3 cursor-pointer relative"
          >
            <span>My Uploads</span>
            {activeTab === 1 && (
              <div className="h-1 bg-gradient-to-r from-pink-500 to-blue-500 mt-1 rounded-lg absolute top-6 bottom-0 left-0 right-0"></div>
            )}
          </div>
          <div
            onClick={() => {
              console.log(
                "Switching to Sourced Profile tab, current jobRoleData:",
                jobRoleData
              );
              console.log("Current selectedID:", selectedID);
              setActiveTab(2);
            }}
            className="px-3 cursor-pointer relative"
          >
            <span>Sourced Profiles</span>
            {activeTab === 2 && (
              <div className="h-1 bg-gradient-to-r from-pink-500 to-blue-500 mt-1 rounded-lg absolute top-6 bottom-0 left-0 right-0"></div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 mt-6">
        {activeTab === 1 ? (
          jobRoleData ? (
            <ActivityOrgAdmin key={selectedID} />
          ) : (
            <div className="flex min-h-screen items-center justify-center">
              <div className="flex flex-col items-center text-center">
                <Loader />
                <p className="dark:text-[#8893C8] text-textSecondary text-sm mt-4">
                  Loading candidates...
                </p>
              </div>
            </div>
          )
        ) : // Sourced Profile Tab
          jobRoleData && selectedID ? (
            <SourcedCandidates
              sourcedJobRole={jobRoleData.job_role || jobRoleData.jobrole}
              sourcedJobId={selectedID}
            />
          ) : isPositionsLoading ? (
            <div className="flex min-h-screen items-center justify-center">
              <div className="flex flex-col items-center text-center">
                <Loader />
                <p className="dark:text-[#8893C8] text-textSecondary text-sm mt-4">
                  Loading candidates...
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-400">
              <p>Please select a job position to view sourced profiles</p>
            </div>
          )}
      </div>

      {/* Job Description Modal */}
      {showJD && (
        <div className="fixed inset-0 flex items-center justify-center bg-opacity-5 backdrop-blur-xl z-[700] p-4">
          <div
            ref={jdRef}
            className="flex flex-col dark:glass-effect rounded-lg shadow-lg backdrop-blur-[500px] w-full max-w-5xl h-[80vh] relative"
          >
            {/* Fixed Header */}
            <div className="flex justify-between items-start p-6 pb-4 gap-4 flex-shrink-0">
              <div className="flex flex-col">
                <span className="text-xs tracking-wide text-textSecondary dark:text-gray-400">
                  Job Role
                </span>
                <h2 className="text-lg font-semibold text-textPrimary dark:text-white mt-1">
                  {modalJobTitle}
                </h2>
                {modalExperience && (
                  <span className="text-sm text-textSecondary dark:text-gray-400">
                    {modalExperience}
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowJD(false)}
                className="hover:text-opacity-80 dark:text-white text-textPrimary"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Scrollable Content */}
            <div className="px-6 pb-6 space-y-6 overflow-y-auto overflow-x-hidden flex-1">
              {jobDescription && (
                <div>
                  {formatJobDescription(
                    jobRoleData?.jobrole || jobRoleData?.job_role,
                    jobDescription
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobApplicants;
